import { CommonModule } from '@angular/common';
import { Component, inject, input, computed, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { map, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { DocumentTemplateService } from '../../services/document-template.service';
import { DocumentMetadata } from '../../types/document-metadata';
import { OcrResultComponent } from '../../components/ocr-result/ocr-result.component';
import { MatIconModule } from '@angular/material/icon';
import { ResizableContainerComponent } from '../../components/resizable-container/resizable-container.component';
import { DocumentViewerComponent } from '../../components/document-viewer/document-viewer.component';
import { sourceKindFromKey } from '../../utils/document-formats';

@Component({
  selector: 'app-ocr-document-viewer-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    OcrResultComponent,
    MatIconModule,
    ResizableContainerComponent,
    DocumentViewerComponent
  ],
  templateUrl: './ocr-document-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host {
      display: block;
      height: 100%;
    }
  `]
})
export class OcrDocumentViewerPageComponent {
  public isDetached = input<boolean>(true);
  /** When provided (embedded mode), this document is used directly instead of route-based fetching. */
  public documentInput = input<DocumentMetadata | null>(null);

  private route = inject(ActivatedRoute);
  private documentService = inject(DocumentTemplateService);

  readonly s3Key$: Observable<string | null> = this.route.paramMap.pipe(
    map(params => params.get('s3Key')),
    switchMap((paramKey) => paramKey ? of(paramKey) : this.route.queryParamMap.pipe(map(q => q.get('s3Key'))))
  );

  readonly routeDocument$: Observable<DocumentMetadata | null> = this.s3Key$.pipe(
    switchMap((s3Key) => s3Key ? this.documentService.getProcessingMetadataOrNull(s3Key) : of(null))
  );

  private readonly routeDocument = toSignal<DocumentMetadata | null>(this.routeDocument$, { initialValue: null });

  /** Resolved document: prefer direct input over route-based fetch */
  readonly document = computed(() => this.documentInput() ?? this.routeDocument());

  /* Both URLs come off whichever document source is active; `app-document-viewer`
     sanitizes the PDF one itself, which is what collapses the separate route and
     input paths this component used to keep in sync by hand. */
  readonly pdfUrl = computed(() => this.document()?.signedS3Url ?? null);
  readonly markdownUrl = computed(() => this.document()?.signedMarkdownS3Url ?? null);
  readonly sourceKind = computed(() => sourceKindFromKey(this.document()?.s3Key));
}
